import { UIEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDays, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@components/ui/dialog';
import { cn } from '@utils/cn';
import { SchedulerShellProps } from './types';

const SCROLL_THRESHOLD = 4;
const EDGE_OFFSET = 8;

export const SchedulerShell = ({
  open,
  onOpenChange,
  title,
  subtitle,
  renderWhen,
  summary,
  children,
  footerExtra,
  footer,
  popoverRef,
  toolbar,
  overlay,
  onClose,
}: SchedulerShellProps) => {
  const { t } = useTranslation('app');
  const whenRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const lastScroll = useRef(0);
  const [pinned, setPinned] = useState(false);
  const [pinnedOpen, setPinnedOpen] = useState(false);
  const [compact, setCompact] = useState(false);
  const [footerHeight, setFooterHeight] = useState(0);

  useEffect(() => {
    const footerEl = footerRef.current;
    if (!open || !footerEl || compact) return;
    const observer = new ResizeObserver(() =>
      setFooterHeight((prev) => Math.max(prev, footerEl.offsetHeight)),
    );
    observer.observe(footerEl);
    return () => observer.disconnect();
  }, [open, compact]);

  useEffect(() => {
    if (open) return;
    setPinned(false);
    setPinnedOpen(false);
    setCompact(false);
    setFooterHeight(0);
    lastScroll.current = 0;
  }, [open]);

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const when = whenRef.current;
    const y = el.scrollTop;
    const nextPinned =
      Boolean(when && summary) && y > (when?.offsetTop || 0) + (when?.offsetHeight || 0) - 12;
    setPinned(nextPinned);
    if (!nextPinned) setPinnedOpen(false);

    const delta = y - lastScroll.current;
    const atEdge = y < EDGE_OFFSET || y + el.clientHeight >= el.scrollHeight - SCROLL_THRESHOLD;
    if (atEdge) setCompact(false);
    else if (delta > SCROLL_THRESHOLD) setCompact(true);
    else if (delta < -SCROLL_THRESHOLD) setCompact(false);
    lastScroll.current = y;
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => (!value && onClose ? onClose() : onOpenChange(value))}>
      <DialogContent
        hideCloseIcon
        className="flex h-[100dvh] max-h-none w-full max-w-none flex-col gap-0 overflow-hidden rounded-none border-0 p-0">
        <header className="relative z-20 flex items-center gap-2 border-b border-neutral-100 bg-white py-2 pl-4 pr-1.5 shadow-[0_2px_6px_rgba(70,75,83,.08)]">
          <div className="min-w-0 flex-1">
            <DialogTitle className="text-lg font-bold leading-tight text-neutral-800">
              {title}
            </DialogTitle>
            {subtitle && (
              <DialogDescription className="mt-0.5 text-sm text-neutral-500">
                {subtitle}
              </DialogDescription>
            )}
          </div>
          <button
            type="button"
            aria-label={t('actions.close')}
            onClick={() => (onClose ? onClose() : onOpenChange(false))}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-neutral-600 hover:bg-neutral-50">
            <X className="h-5 w-5" />
          </button>
        </header>
        {toolbar}
        <div className="relative flex min-h-0 flex-1 flex-col">
          <div
            onScroll={handleScroll}
            onClickCapture={(e) => {
              if (pinnedOpen && !(e.target as HTMLElement).closest('[data-pinned]'))
                setPinnedOpen(false);
            }}
            style={{ paddingBottom: footerHeight + 16 }}
            className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overscroll-contain bg-neutral-100 pt-2">
            {renderWhen && (
              <div ref={whenRef} className="shrink-0 bg-white px-4 pb-3 pt-2">
                {renderWhen('scheduler')}
              </div>
            )}
            {children}
          </div>
          <div
            data-pinned
            aria-hidden={!pinned}
            className={cn(
              'absolute inset-x-0 top-0 z-10 transition-opacity duration-150 motion-reduce:transition-none',
              pinned ? 'visible opacity-100' : 'invisible opacity-0',
            )}>
            <button
              type="button"
              aria-expanded={pinnedOpen}
              tabIndex={pinned ? 0 : -1}
              onClick={() => setPinnedOpen((prev) => !prev)}
              className="flex min-h-12 w-full items-center justify-between gap-2 border-b border-neutral-100 bg-white px-4 text-left text-base tabular-nums text-neutral-800 shadow-[0_4px_10px_rgba(70,75,83,.12)]">
              <span className="flex min-w-0 items-center gap-2 truncate">
                <CalendarDays className="h-4 w-4 shrink-0 text-neutral-500" />
                {summary}
              </span>
              {pinnedOpen ? (
                <ChevronUp className="h-5 w-5 shrink-0" />
              ) : (
                <ChevronDown className="h-5 w-5 shrink-0" />
              )}
            </button>
            {pinned && pinnedOpen && renderWhen && (
              <div className="max-h-[75vh] overflow-y-auto rounded-b-lg bg-white px-4 pb-4 pt-3 shadow-[0_12px_24px_rgba(30,34,39,.22)]">
                {renderWhen('scheduler-pinned')}
              </div>
            )}
          </div>
          <div
            ref={footerRef}
            className={cn(
              'absolute inset-x-0 bottom-0 z-20 flex flex-col gap-2 border-t border-neutral-100 bg-white px-3 pb-[calc(10px+env(safe-area-inset-bottom,0px))] pt-2.5 shadow-[0_-8px_30px_rgba(0,0,0,.12)] transition-[padding] duration-150 motion-reduce:transition-none',
              compact && 'gap-0 pb-[calc(6px+env(safe-area-inset-bottom,0px))] pt-1.5',
            )}>
            {footerExtra && (
              <div
                className={cn(
                  'max-h-11 overflow-hidden transition-[max-height,opacity] duration-150 motion-reduce:transition-none',
                  compact && 'invisible max-h-0 opacity-0',
                )}>
                {footerExtra}
              </div>
            )}
            {footer}
          </div>
        </div>
        {overlay}
        {popoverRef && <div ref={popoverRef} className="overflow-visible" />}
      </DialogContent>
    </Dialog>
  );
};
